import { createClient } from "@sanity/client";
import { createImageUrlBuilder } from "@sanity/image-url";

const client = createClient({
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID || "u5tuo4ra",
  dataset: process.env.REACT_APP_SANITY_DATASET || "production",
  apiVersion: "2024-01-01",
  useCdn: true,
  perspective: "published",
});

const builder = createImageUrlBuilder(client);
const urlFor = (source) => builder.image(source).url();

/**
 * Extract plain text from Portable Text blocks for excerpt preview.
 */
function extractTextFromBody(body = []) {
  return body
    .filter((block) => block._type === "block" && block.children)
    .map((block) =>
      block.children
        .filter((child) => child._type === "span")
        .map((child) => child.text)
        .join("")
    )
    .join(" ")
    .trim();
}

function formatDate(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  return date.toLocaleDateString("ro-RO", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const CATEGORY_LABELS = {
  "pelerinaje-interne": "Pelerinaje Interne",
  "pelerinaje-externe": "Pelerinaje Externe",
};

function normalizePost(post) {
  const featuredImage = post.image ? urlFor(post.image) : null;
  const excerpt = extractTextFromBody(post.body);

  return {
    id: post._id,
    slug: post.slug?.current || "",
    title: post.title || "",
    excerpt,
    body: post.body || [],
    date: formatDate(post.publishedAt),
    featuredImage,
    authorName: "Pelerinul Crestin",
    category: post.category || null,
    categoryLabel: CATEGORY_LABELS[post.category] || null,
    cost: post.cost || null,
  };
}

/**
 * Fetch a paginated list of published posts from Sanity.
 * @param {number} page - Page number (1-based)
 * @param {number} perPage - Number of posts per page
 * @param {string|null} category - Optional category filter ('pelerinaje-interne' | 'pelerinaje-externe')
 * @returns {Promise<{posts: Array, totalPages: number}>}
 */
export async function getPosts(page = 1, perPage = 6, category = null) {
  const start = (page - 1) * perPage;
  const end = start + perPage;

  const params = {};
  const categoryFilter = category
    ? " && category == $category"
    : "";
  if (category) params.category = category;

  const [posts, totalCount] = await Promise.all([
    client.fetch(
      `*[_type == "post" && defined(slug.current)${categoryFilter}] | order(publishedAt desc) [${start}...${end}] {
        _id, title, slug, publishedAt, image, body, category, cost
      }`,
      params
    ),
    client.fetch(
      `count(*[_type == "post" && defined(slug.current)${categoryFilter}])`,
      params
    ),
  ]);

  const totalPages = Math.ceil(totalCount / perPage) || 1;
  return { posts: posts.map(normalizePost), totalPages };
}

/**
 * Fetch a single post by slug from Sanity.
 * @param {string} slug
 * @returns {Promise<Object>}
 */
/**
 * Fetch the latest pilgrimages for the homepage slider, filtered by category.
 * @param {string} category - 'pelerinaje-interne' | 'pelerinaje-externe'
 * @param {number} limit - Max number of results
 * @returns {Promise<Array>}
 */
export async function getPilgrimagesForSlider(category, limit = 9) {
  const posts = await client.fetch(
    `*[_type == "post" && defined(slug.current) && category == $category] | order(publishedAt desc) [0...$limit] {
      _id, title, slug, publishedAt, image, body, category, cost
    }`,
    { category, limit }
  );
  return posts.map(normalizePost);
}

export async function getPostBySlug(slug) {
  const post = await client.fetch(
    `*[_type == "post" && slug.current == $slug][0] {
      _id, title, slug, publishedAt, image, body, category, cost
    }`,
    { slug }
  );

  if (!post) {
    throw new Error("Articolul nu a fost gasit.");
  }

  return normalizePost(post);
}
